/**
 * Message Renderer
 * Template function for rendering individual messages
 */

import { html, type TemplateResult } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import type { ChatMessage, ButtonOption } from '../types.js';
import { formatMessageTime } from '../../../utils/chat-utils.js';
import '../../cards/mariana-recipe-cards.js';
import '../../cards/mariana-coupon-cards.js';
import '../../cards/mariana-shop-cards.js';
import '../../cards/mariana-images.js';
import '../../buttons/mariana-csat-buttons.js';
import '../../buttons/mariana-menu-buttons.js';

marked.use({ breaks: true });

/**
 * Calculate message rendering flags
 */
function getMessageFlags(msg: ChatMessage) {
  const messageText = typeof msg.message === 'string' ? msg.message : '';
  const hasText = messageText.trim().length > 0;
  const hasHierarchyButtons = msg.hierarchy_buttons && msg.hierarchy_buttons.length > 0;
  const hasNormalButtons = msg.normal_buttons && msg.normal_buttons.length > 0;
  const hasListButtons = msg.list_buttons && msg.list_buttons.length > 0;
  const hasCsatButtons = msg.csat && msg.csat.length > 0;
  const hasRecipes = msg.recipes && msg.recipes.length > 0;
  const hasCoupons = msg.coupons && msg.coupons.length > 0;
  const hasShops = msg.shops && msg.shops.length > 0;
  const hasImages = !!msg.image;

  // Detect if normal_buttons contains menu-structured items (with type field)
  const isMenuStructure = hasNormalButtons
    && 'type' in msg.normal_buttons![0];

  return {
    hasText,
    hasHierarchyButtons,
    hasNormalButtons: hasNormalButtons && !isMenuStructure,
    hasMenuButtons: hasNormalButtons && isMenuStructure,
    hasListButtons,
    hasCsatButtons,
    hasRecipes,
    hasCoupons,
    hasShops,
    hasImages,
    isButtonsOnly: !hasText && (hasHierarchyButtons || hasNormalButtons || hasListButtons || hasCsatButtons) && !hasRecipes && !hasCoupons && !hasShops,
    showAvatar: msg.direction === 'incoming' && hasText
  };
}

/**
 * Adjusts the primary (first) button label to reflect the number of selected coupons.
 * "Recuperar cupão" → "Recuperar cupões (N)" when N > 1.
 * "Recuperar cupão" → "Recuperar cupão (1)" when N === 1.
 */
function adjustPrimaryButtonLabel(buttons: ButtonOption[], count: number): ButtonOption[] {
  if (count <= 0 || buttons.length === 0) return buttons;

  const primary = buttons[0];
  const label = primary.label.toLowerCase();

  // Only adjust buttons whose label looks like "recuperar cupão/cupões"
  if (!label.includes('recuperar')) return buttons;

  const newLabel = count > 1
    ? `Recuperar cupões (${count})`
    : `Recuperar cupão (${count})`;

  return [{ ...primary, label: newLabel }, ...buttons.slice(1)];
}

/**
 * Renders a single chat message
 */
export function renderMessage(
  msg: ChatMessage,
  onButtonClick: (e: CustomEvent<ButtonOption>) => void,
  selectedCouponCount = 0,
  source = ''
): TemplateResult {
  const flags = getMessageFlags(msg);
  const messageText = typeof msg.message === 'string' ? msg.message : '';
  const renderedMessageText =
    msg.direction === 'incoming'
      ? unsafeHTML(DOMPurify.sanitize(marked.parse(messageText) as string))
      : messageText;
  const formattedTime = formatMessageTime(msg.sentTime);

  // Only the primary hierarchy button is disabled when no coupons selected
  // and the button is actually coupon-related (label includes "recuperar")
  const isCouponRelated = flags.hasHierarchyButtons
    && msg.hierarchy_buttons![0]?.label.toLowerCase().includes('recuperar');
  const primaryDisabled = isCouponRelated && selectedCouponCount === 0;

  // Adjust primary button label based on selected coupon count
  const hierarchyButtons = flags.hasHierarchyButtons
    ? adjustPrimaryButtonLabel(msg.hierarchy_buttons!, selectedCouponCount)
    : msg.hierarchy_buttons;

  return html`
    <div class="message ${msg.direction} ${flags.isButtonsOnly ? 'buttons-only' : ''} ${flags.hasRecipes ? 'has-recipes' : ''} ${flags.hasCoupons ? 'has-coupons' : ''} ${flags.hasShops ? 'has-shops' : ''} ${flags.hasImages ? 'has-images' : ''}">
      ${flags.showAvatar ? html`
        <div class="message-avatar"></div>
      ` : ''}    
      <div class="message-content">
        ${flags.hasText ? html`
          <div class="message-timestamp">${formattedTime}</div>
          <div class="message-bubble">
            <div class="message-text">${renderedMessageText}</div>
          </div>
        ` : ''}
        
        ${flags.hasHierarchyButtons ? html`
          <mariana-hierarchy-buttons
            .buttons=${hierarchyButtons}
            ?disabled=${primaryDisabled}
            ?data-action-locked=${!!msg.actionsLocked}
            @button-click=${onButtonClick}
          ></mariana-hierarchy-buttons>
        ` : ''}
        
        ${flags.hasNormalButtons ? html`
          <mariana-normal-buttons
            .buttons=${msg.normal_buttons}
            ?data-action-locked=${!!msg.actionsLocked}
            @button-click=${onButtonClick}
          ></mariana-normal-buttons>
        ` : ''}
        
        ${flags.hasListButtons ? html`
          <mariana-list-button
            .buttons=${msg.list_buttons}
            ?data-action-locked=${!!msg.actionsLocked}
            @button-click=${onButtonClick}
          ></mariana-list-button>
        ` : ''}

        ${flags.hasMenuButtons ? html`
          <mariana-menu-buttons
            .buttons=${msg.normal_buttons}
            .selectedValue=${msg.lockedSelectedValue ?? ''}
            ?data-action-locked=${!!msg.actionsLocked}
            @button-click=${onButtonClick}
          ></mariana-menu-buttons>
        ` : ''}

        ${flags.hasCsatButtons ? html`
          <mariana-csat-buttons
            .buttons=${msg.csat}
            ?data-action-locked=${!!msg.actionsLocked}
            @button-click=${onButtonClick}
          ></mariana-csat-buttons>
        ` : ''}

        ${flags.hasRecipes ? html`
          <mariana-recipe-cards
            .recipes=${msg.recipes}
            .source=${source}
            ?data-action-locked=${!!msg.actionsLocked}
            @recipe-action=${onButtonClick}
          ></mariana-recipe-cards>
        ` : ''}

        ${flags.hasCoupons ? html`
          <mariana-coupon-cards
            .coupons=${msg.coupons}
            ?data-action-locked=${!!msg.actionsLocked}
            @coupon-action=${onButtonClick}
          ></mariana-coupon-cards>
        ` : ''}

        ${flags.hasShops ? html`
          <mariana-shop-cards
            .shops=${msg.shops}
            .source=${source}
          ></mariana-shop-cards>
        ` : ''}

        ${flags.hasImages ? html`
          <mariana-images
            .images=${msg.image ? [msg.image] : []}
            .source=${source}
          ></mariana-images>
        ` : ''}
      </div>
    </div>
  `;
}

/**
 * Renders typing indicator
 */
export function renderTypingIndicator(): TemplateResult {
  return html`
    <div class="message incoming">
      <div class="message-avatar"></div>
      <div class="typing-indicator">
        <div class="typing-dots">
          <span></span>
          <span></span>
          <span></span>
        </div>
      </div>
    </div>
  `;
}
